import React from 'react';
import { styled } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { Badge, IconButton, Toolbar, Typography } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { getThemeMode, darkMode, toggleThemeMode } from '../store/themeSlice';

interface StyledAppBarProps extends MuiAppBarProps {
	open: boolean;
	drawerwidth: number;
}

const StyledAppBar = styled(MuiAppBar, {
	shouldForwardProp: (prop) => prop !== 'open',
})<StyledAppBarProps>(({ theme, open, drawerwidth }) => ({
	zIndex: theme.zIndex.drawer + 1,
	transition: theme.transitions.create(['width', 'margin'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.leavingScreen,
	}),
	...(open && {
		marginLeft: drawerwidth,
		width: `calc(100% - ${drawerwidth}px)`,
		transition: theme.transitions.create(['width', 'margin'], {
			easing: theme.transitions.easing.sharp,
			duration: theme.transitions.duration.enteringScreen,
		}),
	}),
}));

interface AppBarProps extends StyledAppBarProps {
	toggleDrawer: () => void;
	title?: string;
}

export default function AppBar({ open, drawerwidth, toggleDrawer, title }: AppBarProps) {
	const themeMode = useAppSelector(getThemeMode);
	const dispatch = useAppDispatch();

	return (
		<StyledAppBar position="absolute" open={open} drawerwidth={drawerwidth}>
			<Toolbar
				sx={{
					pr: '24px', // keep right padding when drawer closed
				}}
			>
				<IconButton
					edge="start"
					color="inherit"
					aria-label="open drawer"
					onClick={toggleDrawer}
					sx={{
						marginRight: '36px',
						...(open && { display: 'none' }),
					}}
				>
					<MenuIcon />
				</IconButton>
				<Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
					{title}
				</Typography>
				<IconButton color="inherit">
					<Badge badgeContent={4} color="secondary">
						<NotificationsIcon />
					</Badge>
				</IconButton>
				<IconButton color="inherit" onClick={() => dispatch(toggleThemeMode())}>
					{themeMode === darkMode ? <LightModeIcon /> : <DarkModeIcon />}
				</IconButton>
			</Toolbar>
		</StyledAppBar>
	);
}
